import { useEffect, useState, type PointerEvent } from 'react';

import { Box, ButtonBase, Paper, Slider, TextField, Typography } from '@mui/material';

import { useWallStore, useWorkspaceUiStore } from '@walls/store';
import { getPanelColor, getPanelTitleColor } from '../theme/wallThemeStyles';

const hexColorPattern = /^#[0-9a-f]{6}$/i;
const shapeColors = [
  '#000000',
  '#666666',
  '#FFFFFF',
  '#FF0000',
  '#FF9900',
  '#FFCC4D',
  '#33CC66',
  '#00CCFF',
  '#3366FF',
  '#9933FF',
  '#E1386A',
  '#8B572A',
];

interface ShapeToolDialogProps {
  layoutStyle?: string;
}

export function ShapeToolDialog({ layoutStyle }: ShapeToolDialogProps) {
  const [selectedSection, setSelectedSection] = useState<'fill' | 'border'>('fill');
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const selectedShape = useWorkspaceUiStore((state) => state.selectedShape);
  const selectedLineColor = useWorkspaceUiStore((state) => state.selectedLineColor);
  const selectedLineThickness = useWorkspaceUiStore((state) => state.selectedLineThickness);
  const selectedFillColor = useWorkspaceUiStore((state) => state.selectedFillColor);
  const selectedFillOpacity = useWorkspaceUiStore((state) => state.selectedFillOpacity);
  const selectedPenColor = useWorkspaceUiStore((state) => state.selectedPenColor);
  const selectedPenThickness = useWorkspaceUiStore((state) => state.selectedPenThickness);
  const setSelectedTool = useWorkspaceUiStore((state) => state.setSelectedTool);
  const setSelectedShape = useWorkspaceUiStore((state) => state.setSelectedShape);
  const setSelectedLineColor = useWorkspaceUiStore((state) => state.setSelectedLineColor);
  const setSelectedLineThickness = useWorkspaceUiStore((state) => state.setSelectedLineThickness);
  const setSelectedFillColor = useWorkspaceUiStore((state) => state.setSelectedFillColor);
  const setSelectedFillOpacity = useWorkspaceUiStore((state) => state.setSelectedFillOpacity);
  const setSelectedPenColor = useWorkspaceUiStore((state) => state.setSelectedPenColor);
  const setSelectedPenThickness = useWorkspaceUiStore((state) => state.setSelectedPenThickness);
  const theme = useWallStore((state) => state.theme);
  const panelColor = getPanelColor(theme);
  const panelTitleColor = getPanelTitleColor(theme);
  const isLineSelected = selectedTool === 'line';

  const selectShape = (shape: string) => {
    setSelectedTool('shape');
    setSelectedShape(shape);
  };

  return (
    <Paper
      elevation={0}
      onClick={(event) => event.stopPropagation()}
      sx={{
        bgcolor: panelColor,
        color: panelTitleColor,
        boxShadow: '0px 3px 6px #00000029',
        bottom: {
          xs: layoutStyle === 'open' ? 'auto' : 10,
          sm: 'auto',
        },
        left: { xs: 68, sm: 78 },
        maxHeight: 'calc(100vh - 20px)',
        overflowY: 'auto',
        position: 'fixed',
        top: {
          xs: layoutStyle === 'open' ? 70 : 'auto',
          sm: '50%',
        },
        transform: { xs: 'none', sm: 'translateY(-50%)' },
        width: { xs: 'min(235px, calc(100vw - 78px))', sm: 235 },
        zIndex: 1001,
      }}
    >
      <Box sx={{ borderBottom: '2px solid #f3f5f8', display: 'flex' }}>
        <ShapeSelectButton
          label="Line"
          selected={isLineSelected}
          onClick={() => setSelectedTool('line')}
          icon={<LineShapeIcon />}
          panelTitleColor={panelTitleColor}
        />
        <ShapeSelectButton
          label="Rectangle"
          selected={!isLineSelected && selectedShape === 'rectangle'}
          onClick={() => selectShape('rectangle')}
          icon={<RectangleShapeIcon />}
          panelTitleColor={panelTitleColor}
        />
        <ShapeSelectButton
          label="Circle"
          selected={!isLineSelected && selectedShape === 'circle'}
          onClick={() => selectShape('circle')}
          icon={<CircleShapeIcon />}
          panelTitleColor={panelTitleColor}
        />
      </Box>

      {isLineSelected ? (
        <Box sx={{ p: 1.25 }}>
          <Typography sx={sectionTitleSx}>Width</Typography>
          <Slider
            aria-label="Line width"
            max={20}
            min={1}
            onChange={(_, value) => setSelectedLineThickness(asSliderValue(value))}
            size="small"
            value={selectedLineThickness}
          />
          <ColorControl
            label="Line color"
            previewColor={selectedLineColor}
            value={selectedLineColor}
            onChange={setSelectedLineColor}
          />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', mb: 0.625 }}>
            <SectionButton
              label="Fill"
              selected={selectedSection === 'fill'}
              onClick={() => setSelectedSection('fill')}
              panelColor={panelColor}
            />
            <SectionButton
              label="Border"
              selected={selectedSection === 'border'}
              onClick={() => setSelectedSection('border')}
              panelColor={panelColor}
            />
          </Box>

          {selectedSection === 'fill' ? (
            <Box sx={{ p: 1.25 }}>
              <Typography sx={sectionTitleSx}>Opacity</Typography>
              <Slider
                aria-label="Shape fill opacity"
                max={100}
                min={0}
                onChange={(_, value) => setSelectedFillOpacity(asSliderValue(value))}
                size="small"
                value={selectedFillOpacity}
              />
              <ColorControl
                label="Fill color"
                previewColor={toRgba(selectedFillColor, selectedFillOpacity)}
                value={selectedFillColor}
                onChange={setSelectedFillColor}
              />
            </Box>
          ) : (
            <Box sx={{ p: 1.25 }}>
              <Typography sx={sectionTitleSx}>Width</Typography>
              <Slider
                aria-label="Shape border width"
                max={20}
                min={1}
                onChange={(_, value) => setSelectedPenThickness(asSliderValue(value))}
                size="small"
                value={selectedPenThickness}
              />
              <ColorControl
                label="Border color"
                previewColor={selectedPenColor}
                value={selectedPenColor}
                onChange={setSelectedPenColor}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

function ShapeSelectButton({
  icon,
  label,
  onClick,
  panelTitleColor,
  selected,
}: {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  panelTitleColor: string;
  selected: boolean;
}) {
  return (
    <ButtonBase
      aria-label={label}
      onClick={onClick}
      sx={{
        bgcolor: selected ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
        color: panelTitleColor,
        flex: 1,
        height: 66,
        p: 1.25,
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
      }}
    >
      {icon}
    </ButtonBase>
  );
}

function SectionButton({
  label,
  onClick,
  panelColor,
  selected,
}: {
  label: string;
  onClick: () => void;
  panelColor: string;
  selected: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        bgcolor: selected ? panelColor : 'rgba(0, 0, 0, 0.25)',
        flex: 1,
        fontSize: 14,
        p: '6px',
      }}
    >
      {label}
    </ButtonBase>
  );
}

export function ColorControl({
  label,
  onChange,
  previewColor,
  value,
}: {
  label: string;
  onChange: (color: string) => void;
  previewColor: string;
  value: string;
}) {
  const [typedValue, setTypedValue] = useState(value);
  const displayValue = typedValue === value ? value : typedValue;

  useEffect(() => {
    setTypedValue(value);
  }, [value]);

  const commitColor = (color: string) => {
    const normalizedColor = normalizeHexColor(color);

    setTypedValue(normalizedColor);
    if (hexColorPattern.test(normalizedColor)) {
      onChange(normalizedColor);
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <InlineColorPicker label={label} value={value} onChange={commitColor} />
      <ColorSwatchGrid label={label} value={value} onChange={commitColor} />
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <Box
          aria-hidden="true"
          sx={{
            bgcolor: previewColor,
            border: '1px solid #666666',
            height: 23,
            width: 75,
          }}
        />
        <TextField
          aria-label={`${label} hex value`}
          onBlur={() => setTypedValue(value)}
          onChange={(event) => commitColor(event.target.value)}
          size="small"
          value={displayValue}
          variant="outlined"
          inputProps={{
            maxLength: 7,
            sx: {
              fontSize: 14,
              height: 19,
              p: '2px 4px',
              textAlign: 'center',
              width: 75,
            },
          }}
        />
      </Box>
    </Box>
  );
}

function InlineColorPicker({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (color: string) => void;
  value: string;
}) {
  const hsvColor = hexToHsv(value) ?? { h: 0, s: 1, v: 1 };
  const hueColor = `hsl(${hsvColor.h}, 100%, 50%)`;

  const updateSaturationValue = (event: PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const saturation = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const brightness = clamp(1 - (event.clientY - rect.top) / rect.height, 0, 1);

    onChange(hsvToHex({ ...hsvColor, s: saturation, v: brightness }));
  };

  const updateHue = (event: PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const hue = clamp((event.clientX - rect.left) / rect.width, 0, 1) * 360;

    onChange(hsvToHex({ ...hsvColor, h: hue }));
  };

  return (
    <Box sx={{ display: 'grid', gap: 0.75 }}>
      <Box
        role="slider"
        aria-label={`${label} saturation and brightness`}
        aria-valuetext={value}
        tabIndex={0}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateSaturationValue(event);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            updateSaturationValue(event);
          }
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
        }}
        sx={{
          background: `linear-gradient(to top, #000000, transparent), linear-gradient(to right, #ffffff, ${hueColor})`,
          cursor: 'crosshair',
          height: 110,
          position: 'relative',
          touchAction: 'none',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            border: '2px solid #ffffff',
            borderRadius: '50%',
            boxShadow: '0 0 0 1px #0f2a39',
            height: 12,
            left: `${hsvColor.s * 100}%`,
            pointerEvents: 'none',
            position: 'absolute',
            top: `${(1 - hsvColor.v) * 100}%`,
            transform: 'translate(-50%, -50%)',
            width: 12,
          }}
        />
      </Box>
      <Box
        role="slider"
        aria-label={`${label} hue`}
        aria-valuenow={Math.round(hsvColor.h)}
        aria-valuemin={0}
        aria-valuemax={360}
        tabIndex={0}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateHue(event);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            updateHue(event);
          }
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
        }}
        sx={{
          background:
            'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
          cursor: 'ew-resize',
          height: 14,
          position: 'relative',
          touchAction: 'none',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            border: '2px solid #ffffff',
            borderRadius: '50%',
            boxShadow: '0 0 0 1px #0f2a39',
            height: 16,
            left: `${(hsvColor.h / 360) * 100}%`,
            pointerEvents: 'none',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 16,
          }}
        />
      </Box>
    </Box>
  );
}

function ColorSwatchGrid({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (color: string) => void;
  value: string;
}) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{
        display: 'grid',
        gap: 0.75,
        gridTemplateColumns: 'repeat(6, 1fr)',
      }}
    >
      {shapeColors.map((color) => {
        const selected = color.toLowerCase() === value.toLowerCase();

        return (
          <Box
            key={color}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`${label} ${color}`}
            onClick={() => onChange(color)}
            sx={{
              bgcolor: color,
              border: selected ? '2px solid #0f2a39' : '1px solid #c7ced6',
              borderRadius: '50%',
              boxShadow:
                color === '#FFFFFF'
                  ? 'inset 0 0 0 1px #e4e7ec'
                  : selected
                  ? '0 0 0 2px #d8f7ff'
                  : undefined,
              cursor: 'pointer',
              height: 27,
              justifySelf: 'center',
              p: 0,
              width: 27,
            }}
          />
        );
      })}
    </Box>
  );
}

function LineShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <line x1="8" y1="34" x2="34" y2="8" stroke="#0f2a39" strokeWidth="2" />
    </svg>
  );
}

function RectangleShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <rect x="5" y="5" width="32" height="32" fill="#ffffff" stroke="#0f2a39" strokeWidth="2" />
    </svg>
  );
}

function CircleShapeIcon() {
  return (
    <svg aria-hidden="true" height="42" viewBox="0 0 42 42">
      <circle cx="21" cy="21" r="16" fill="#ffffff" stroke="#0f2a39" strokeWidth="2" />
    </svg>
  );
}

const sectionTitleSx = {
  fontSize: 13,
  fontWeight: 700,
  mb: 0.625,
  textAlign: 'left',
};

function asSliderValue(value: number | number[]) {
  return Array.isArray(value) ? value[0] : value;
}

function normalizeHexColor(value: string) {
  const trimmedValue = value.trim();
  return trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`;
}

function toRgba(hexColor: string, opacity: number) {
  const normalizedColor = hexColor.replace('#', '');

  if (!/^[0-9a-f]{6}$/i.test(normalizedColor)) {
    return hexColor;
  }

  const red = parseInt(normalizedColor.slice(0, 2), 16);
  const green = parseInt(normalizedColor.slice(2, 4), 16);
  const blue = parseInt(normalizedColor.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${opacity / 100})`;
}

function hexToHsv(hexColor: string) {
  const normalizedColor = hexColor.replace('#', '');

  if (!/^[0-9a-f]{6}$/i.test(normalizedColor)) {
    return null;
  }

  const red = parseInt(normalizedColor.slice(0, 2), 16) / 255;
  const green = parseInt(normalizedColor.slice(2, 4), 16) / 255;
  const blue = parseInt(normalizedColor.slice(4, 6), 16) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;

  if (delta !== 0) {
    if (max === red) {
      hue = 60 * (((green - blue) / delta) % 6);
    } else if (max === green) {
      hue = 60 * ((blue - red) / delta + 2);
    } else {
      hue = 60 * ((red - green) / delta + 4);
    }
  }

  return {
    h: hue < 0 ? hue + 360 : hue,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

function hsvToHex({ h, s, v }: { h: number; s: number; v: number }) {
  const chroma = v * s;
  const hueSegment = h / 60;
  const x = chroma * (1 - Math.abs((hueSegment % 2) - 1));
  const match = v - chroma;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hueSegment >= 0 && hueSegment < 1) {
    red = chroma;
    green = x;
  } else if (hueSegment >= 1 && hueSegment < 2) {
    red = x;
    green = chroma;
  } else if (hueSegment >= 2 && hueSegment < 3) {
    green = chroma;
    blue = x;
  } else if (hueSegment >= 3 && hueSegment < 4) {
    green = x;
    blue = chroma;
  } else if (hueSegment >= 4 && hueSegment < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  return `#${toHexChannel(red + match)}${toHexChannel(green + match)}${toHexChannel(blue + match)}`;
}

function toHexChannel(value: number) {
  return Math.round(clamp(value, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
